import { API_BASE } from '../config/api';

const withApiBase = (path) => `${API_BASE}${path}`;

const readErrorPayload = async (response) => {
  const data = await response.json().catch(() => ({}));
  const message = data.error || data.message || `Request failed with status ${response.status}.`;

  const error = new Error(message);
  error.status = response.status;
  error.payload = data;
  throw error;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    return readErrorPayload(response);
  }

  return response.json().catch(() => ({}));
};

export const registerUser = async (payload) => {
  const response = await fetch(withApiBase('/api/auth/register'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const buildLoginPayload = (credentials = {}) => {
  const provider = credentials.provider || 'password';
  const resolvedProvider = provider === 'email' ? 'password' : provider;

  if (!['password', 'phone', 'google'].includes(resolvedProvider)) {
    throw new Error('Unsupported login provider.');
  }

  if (resolvedProvider === 'google') {
    const googleToken = String(credentials.googleToken || credentials.token || '').trim();
    const email = String(credentials.email || '').trim().toLowerCase();
    const googleId = String(credentials.googleId || '').trim();

    if (!googleToken && (!email || !googleId)) {
      throw new Error('Google account credentials are required.');
    }

    return {
      provider: 'google',
      ...(googleToken ? { googleToken } : {}),
      ...(email ? { email } : {}),
      ...(googleId ? { googleId } : {}),
    };
  }

  if (resolvedProvider === 'phone') {
    const phone = String(credentials.phone || credentials.identifier || '').trim();
    const password = String(credentials.password || '').trim();

    if (!phone || !password) {
      throw new Error('Phone number and password are required.');
    }

    return { provider: 'phone', phone, password };
  }

  const email = String(credentials.email || '').trim().toLowerCase();
  const phone = String(credentials.phone || '').trim();
  const identifier = String(credentials.identifier || '').trim();
  const password = String(credentials.password || '');

  if (!password) {
    throw new Error('Password is required.');
  }

  const resolvedIdentifier = identifier || email || phone;
  const normalizedEmail = email || (resolvedIdentifier.includes('@') ? resolvedIdentifier.toLowerCase() : '');
  const normalizedPhone = phone || (!resolvedIdentifier.includes('@') ? resolvedIdentifier : '');

  if (!normalizedEmail && !normalizedPhone) {
    throw new Error('Email or phone number is required.');
  }

  return {
    provider: 'password',
    ...(normalizedEmail ? { email: normalizedEmail } : { phone: normalizedPhone }),
    password,
  };
};

export const loginUser = async (credentials) => {
  const loginPayload = buildLoginPayload(credentials);
  const isGoogleLogin = loginPayload.provider === 'google';
  const loginUrl = withApiBase(isGoogleLogin ? '/api/auth/google' : '/api/auth/login');
  const requestBody = isGoogleLogin
    ? { token: loginPayload.googleToken }
    : loginPayload;


  const response = await fetch(loginUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  return handleResponse(response);
};


export const requestPhoneOtp = async (payload) => {
  const response = await fetch(withApiBase('/api/auth/phone/request-otp'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

export const verifyPhoneOtp = async (payload) => {
  const response = await fetch(withApiBase('/api/auth/phone/verify-otp'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

export const requestPasswordReset = async (payload) => {
  const response = await fetch(withApiBase('/api/auth/forgot-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

export const resetPassword = async (payload) => {
  const response = await fetch(withApiBase('/api/auth/reset-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};

export const fetchProfile = async (token) => {
  const response = await fetch(withApiBase('/api/auth/me'), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return handleResponse(response);
};

export const updateAffiliation = async (payload, token) => {
  const response = await fetch(withApiBase('/api/auth/affiliation'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};


export const fetchRoleContexts = async (token) => {
  const response = await fetch(withApiBase('/api/auth/me/roles'), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return handleResponse(response);
};

export const leaveAffiliationMembership = async (payload, token) => {
  const response = await fetch(withApiBase('/api/auth/affiliation/membership'), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response);
};
